import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  Trash2,
  Settings,
  X,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Plus,
  Split,
} from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { AvailableVariable, ConditionOperator } from "../../common/ui/types";
import { formatDragVariable } from "../../utils/variableHelper";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import NodeDocsButton from "../../docs/NodeDocsButton";

const OPERATORS = [
  { value: ConditionOperator.EQUALS, label: "Equals" },
  { value: ConditionOperator.NOT_EQUALS, label: "Not Equals" },
  { value: ConditionOperator.CONTAINS, label: "Contains" },
  { value: ConditionOperator.NOT_CONTAINS, label: "Does Not Contain" },
  { value: ConditionOperator.STARTS_WITH, label: "Starts With" },
  { value: ConditionOperator.DOES_NOT_START_WITH, label: "Does Not Start With" },
  { value: ConditionOperator.ENDS_WITH, label: "Ends With" },
  { value: ConditionOperator.DOES_NOT_END_WITH, label: "Does Not End With" },
  { value: ConditionOperator.GREATER_THAN, label: "Greater Than" },
  { value: ConditionOperator.LESS_THAN, label: "Less Than" },
  { value: ConditionOperator.GREATER_THAN_OR_EQUAL, label: "Greater Than or Equal" },
  { value: ConditionOperator.LESS_THAN_OR_EQUAL, label: "Less Than or Equal" },
  { value: ConditionOperator.IS_EMPTY, label: "Is Empty" },
  { value: ConditionOperator.IS_NOT_EMPTY, label: "Is Not Empty" },
  { value: ConditionOperator.EXISTS, label: "Exists" },
  { value: ConditionOperator.DOES_NOT_EXIST, label: "Does Not Exist" },
  { value: "else" as const, label: "Else (Default)" },
];

interface Rule {
  id: string;
  value: string;
  operator: ConditionOperator | "else";
  compareValue: string;
}

export const SwitchNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  /*
   * Map legacy operator strings to ConditionOperator enum values.
   */
  const migrateOperator = (op: string): ConditionOperator | "else" => {
    const OPERATOR_MIGRATION: Record<string, ConditionOperator | "else"> = {
      "==": ConditionOperator.EQUALS,
      "!=": ConditionOperator.NOT_EQUALS,
      ">": ConditionOperator.GREATER_THAN,
      "<": ConditionOperator.LESS_THAN,
      ">=": ConditionOperator.GREATER_THAN_OR_EQUAL,
      "<=": ConditionOperator.LESS_THAN_OR_EQUAL,
      "contains": ConditionOperator.CONTAINS,
      "not_contains": ConditionOperator.NOT_CONTAINS,
      "starts_with": ConditionOperator.STARTS_WITH,
      "does_not_start_with": ConditionOperator.DOES_NOT_START_WITH,
      "ends_with": ConditionOperator.ENDS_WITH,
      "does_not_end_with": ConditionOperator.DOES_NOT_END_WITH,
      "isEmpty": ConditionOperator.IS_EMPTY,
      "isNotEmpty": ConditionOperator.IS_NOT_EMPTY,
      "exists": ConditionOperator.EXISTS,
      "does_not_exist": ConditionOperator.DOES_NOT_EXIST,
      "else": "else",
    };

    // If already a valid enum value, return as-is
    if (Object.values(ConditionOperator).includes(op as ConditionOperator)) {
      return op as ConditionOperator;
    }

    return OPERATOR_MIGRATION[op] || ConditionOperator.EQUALS;
  };

  /*
   * Initialize local state from data.cases (backend format) or data.rules (legacy).
   * We convert backend 'cases' format back to flat 'rules' for the UI.
   */
  const [rules, setRules] = useState<Rule[]>(() => {
    if (data?.rules) return data.rules;

    if (data?.cases && Array.isArray(data.cases)) {
      return data.cases
        .map((c: any) => ({
          id: c.output_index.toString(),
          value: c.conditions?.[0]?.key || "",
          operator: migrateOperator(c.conditions?.[0]?.operator || "=="),
          compareValue: c.conditions?.[0]?.value || "",
        }))
        .sort((a: any, b: any) => parseInt(a.id) - parseInt(b.id));
    }

    return [{ id: "0", value: "", operator: ConditionOperator.EQUALS, compareValue: "" }];
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setDraggedVariable] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState<string | null>(null);

  const { setNodes, deleteElements, getNodes, getEdges } = useReactFlow();

  // Get execution status from node data
  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => {
      e.stopPropagation();
    },
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object") {
      return { inputs: {}, outputs: {} };
    }

    const outputs = nodeData.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nodeData).forEach(([key, value]) => {
      if (key !== "outputs") inputs[key] = value;
    });

    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const availableVariables: AvailableVariable[] = [];

    const ancestorNodes = getAllAncestorNodes(id, nodes, edges);
    ancestorNodes.forEach((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      availableVariables.push({
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      });
    });

    return availableVariables;
  }, [id, getNodes, getEdges, isModalOpen, extractNodeData]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => {
    e.stopPropagation();
    const fullVariable = formatDragVariable(nodeName, variable, type);
    setDraggedVariable(fullVariable);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };

  const handleDrop = (
    e: React.DragEvent,
    ruleIndex: number,
    field: "value" | "compareValue",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");

    if (variable) {
      const newRules = [...rules];
      newRules[ruleIndex][field] = variable;
      setRules(newRules);
    }
    setDraggedVariable(null);
    setIsDraggingOver(null);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(id);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);
  };

  const addRule = () => {
    setRules([
      ...rules,
      {
        id: rules.length.toString(),
        value: "",
        operator: ConditionOperator.EQUALS,
        compareValue: "",
      },
    ]);
  };

  const removeRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    // Re-index IDs to match output handles 0, 1, 2...
    const reindexedRules = newRules.map((rule, i) => ({
      ...rule,
      id: i.toString(),
    }));
    setRules(reindexedRules);
  };

  const updateRule = (index: number, field: keyof Rule, value: string | ConditionOperator | "else") => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setRules(newRules);
  };

  const statusConfig = useMemo(() => {
    if (isExecuting) {
      return { bg: "bg-blue-500", text: "Executing...", pulse: true };
    } else if (executionSuccess) {
      return {
        bg: "bg-green-500",
        text: "Executed Successfully",
        pulse: false,
      };
    } else if (executionError) {
      return { bg: "bg-red-500", text: "Execution Failed", pulse: false };
    }

    const hasConfig =
      rules.length > 0 &&
      rules.every(
        (r) =>
          r.value &&
          (r.operator === ConditionOperator.IS_EMPTY ||
            r.operator === ConditionOperator.IS_NOT_EMPTY ||
            r.operator === ConditionOperator.EXISTS ||
            r.operator === ConditionOperator.DOES_NOT_EXIST ||
            r.operator === "else" ||
            r.compareValue),
      );
    return hasConfig
      ? { bg: "bg-emerald-500", text: "Configured", pulse: false }
      : { bg: "bg-gray-400", text: "Not Configured", pulse: false };
  }, [rules, isExecuting, executionSuccess, executionError]);

  useEffect(() => {
    // Debounce update
    const timeoutId = setTimeout(() => {
      // Transform UI rules to Backend cases
      const cases = rules.map((r, i) => ({
        output_index: i,
        conditions:
          r.operator === "else"
            ? []
            : [
                {
                  key: r.value,
                  operator: r.operator,
                  value: r.compareValue,
                },
              ],
      }));

      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            // Compare against existing cases to avoid loops
            if (JSON.stringify(node.data.cases) === JSON.stringify(cases)) {
              return node;
            }
            return {
              ...node,
              data: {
                ...node.data,
                cases, // Store directly as cases
                rules: undefined, // Cleanup legacy rules if present
              },
            };
          }
          return node;
        }),
      );
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [rules, id, setNodes]);

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;

  return (
    <div
      className={`relative bg-white border-2 rounded-xl min-w-[280px] shadow-sm transition-all duration-200 group
        ${
          isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
              ? "border-green-500 shadow-md shadow-green-200"
              : executionError
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-violet-500 shadow-md scale-105"
                  : "border-gray-200 hover:border-violet-300"
        }
      `}
    >
      {/* Execution Spinner Overlay */}
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}

 {/* Success Overlay */}
      {executionSuccess && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {executionError && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-violet-600 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(139, 92, 246, 0.2), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      {/* Header */}
      <div
        className={`p-3 rounded-t-xl flex items-center justify-between
           ${
             executionSuccess
               ? "bg-green-50"
               : executionError
                 ? "bg-red-50"
                 : "bg-gradient-to-r from-violet-50 to-fuchsia-50"
           }
      `}
      >
        <div className="flex items-center gap-2">
          <div
            className={`p-2 rounded-lg ${
              executionSuccess
                ? "bg-green-100 text-green-600"
                : executionError
                  ? "bg-red-100 text-red-600"
                  : "bg-white text-violet-600 shadow-sm"
            }`}
          >
            {isExecuting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Split className="w-4 h-4" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Switch</h3>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                  statusConfig.pulse ? "animate-pulse" : ""
                }`}
              />
              <p className="text-[10px] text-gray-500">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="SWITCH" />
          <button
            onClick={handleDeleteClick}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-3 space-y-3" onClick={stopPropagation}>
        <button
          onClick={handleOpenModal}
          className="w-full py-2.5 rounded-lg bg-violet-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-violet-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Configure Switch
        </button>

        {/* Rule Indicators / Output Handles */}
        <div className="space-y-1">
          <div className="flex justify-between items-center px-1 mb-2">
            <span className="text-[10px] uppercase font-bold text-gray-400">
              Routes
            </span>
          </div>

          <div className="flex flex-col gap-2">
            {rules.map((rule, index) => (
              <div
                key={rule.id}
                className="relative flex items-center justify-between bg-violet-50/50 border border-violet-100 rounded px-2 py-1.5 h-8 group/item hover:border-violet-300 transition-colors"
              >
                {/* Index Badge */}
                <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center bg-white rounded-full text-[9px] font-bold text-violet-600 border border-violet-100 shadow-sm">
                  {index}
                </span>

                {/* Label */}
                <span
                  className="text-[10px] text-gray-600 font-mono truncate ml-2 text-right flex-1"
                  title={rule.value}
                >
                  {rule.operator === "else"
                    ? "Else"
                    : rule.value || "Condition"}
                </span>

                {/* Handle */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={rule.id}
                  className="!w-2.5 !h-2.5 !bg-violet-600 !border-2 !border-white !rounded-full !absolute !right-[-17px]"
                  style={{
                    boxShadow: "0 0 0 4px rgba(139, 92, 246, 0.1)",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-50 rounded">
                    <Split className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Switch
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Define routing rules
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Three-column Layout */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Left Sidebar - Previous Nodes */}
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>

                {/* Center - Main Configuration */}
                <div className="flex-1 overflow-y-auto bg-white p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Routing Rules
                    </h3>
                    <button
                      onClick={addRule}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Rule
                    </button>
                  </div>

                  <div className="space-y-6">
                    {rules.map((rule, index) => (
                      <div
                        key={index}
                        className="relative p-4 bg-gray-50 rounded-lg border border-gray-200 group"
                      >
                        <div className="absolute -left-3 top-4 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-500 shadow-sm">
                          {index}
                        </div>
                        <button
                          onClick={() => removeRule(index)}
                          className="absolute top-2 right-2 text-gray-400 hover:text-red-500 p-1 rounded transition opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-4 h-4" />
                        </button>

                        <div className="space-y-3">
                          {/* Value 1 */}
                          <div>
                            <input
                              type="text"
                              value={rule.value}
                              onChange={(e) =>
                                updateRule(index, "value", e.target.value)
                              }
                              onDrop={(e) => handleDrop(e, index, "value")}
                              onDragOver={(e) =>
                                handleDragOver(e, `value-${index}`)
                              }
                              onDragLeave={handleDragLeave}
                              placeholder="Value 1"
                              className={`w-full px-3 py-2 bg-white border rounded text-sm text-gray-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition font-mono ${
                                isDraggingOver === `value-${index}`
                                  ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200"
                                  : "border-gray-300 hover:border-violet-400"
                              }`}
                            />
                          </div>

                          {/* Operator */}
                          <div>
                            <select
                              value={rule.operator}
                              onChange={(e) =>
                                updateRule(index, "operator", e.target.value as ConditionOperator | "else")
                              }
                              className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition appearance-none cursor-pointer hover:border-violet-400"
                            >
                              {OPERATORS.map((op) => (
                                <option key={op.value} value={op.value}>
                                  {op.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Value 2 */}
                          {rule.operator !== ConditionOperator.IS_EMPTY &&
                            rule.operator !== ConditionOperator.IS_NOT_EMPTY &&
                            rule.operator !== ConditionOperator.EXISTS &&
                            rule.operator !== ConditionOperator.DOES_NOT_EXIST &&
                            rule.operator !== "else" && (
                              <div>
                                <input
                                  type="text"
                                  value={rule.compareValue}
                                  onChange={(e) =>
                                    updateRule(
                                      index,
                                      "compareValue",
                                      e.target.value,
                                    )
                                  }
                                  onDrop={(e) =>
                                    handleDrop(e, index, "compareValue")
                                  }
                                  onDragOver={(e) =>
                                    handleDragOver(e, `compareValue-${index}`)
                                  }
                                  onDragLeave={handleDragLeave}
                                  placeholder="Value 2"
                                  className={`w-full px-3 py-2 bg-white border rounded text-sm text-gray-700 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition font-mono ${
                                    isDraggingOver === `compareValue-${index}`
                                      ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200"
                                      : "border-gray-300 hover:border-violet-400"
                                  }`}
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    ))}

                    {rules.length === 0 && (
                      <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                        No routing rules defined. Click "Add Rule" to start.
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Sidebar - Outputs */}
                <div className="w-80 bg-gray-50 overflow-y-auto p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Node Outputs
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Available after execution
                  </p>

                  {hasOutputs && (
                    <div className="mt-4 pt-3 border-t border-violet-200">
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">
                        JSON Output
                      </h4>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                        <pre className="whitespace-pre-wrap break-words">
                          <code
                            dangerouslySetInnerHTML={{
                              __html: syntaxHighlight(data.outputs),
                            }}
                          />
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Expected Output Info */}
                  <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">
                      Routing Info
                    </h4>
                    <div className="space-y-2 text-xs text-gray-600">
                      <p>
                        Data will be routed to the first matching rule's output
                        path.
                      </p>
                      <div className="flex flex-col gap-1 mt-2">
                        {rules.map((rule, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center bg-gray-100 rounded text-[9px] font-medium text-gray-600">
                              {index}
                            </span>
                            <span className="truncate">
                              {rule.value || "?"} {rule.operator}{" "}
                              {rule.compareValue}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {statusConfig.text === "Configured" ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-yellow-700">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        Please configure all rules
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SwitchNode;
